import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { useBjModal } from '@hooks/common/useBjModal';
import { useSwrGlobal } from '@hooks/useSwrApi';
import { useSwrDeleteCloseSubscription } from '@hooks/useSwrApi/endpoints/subscription';
import {
  Button, PopoverStyled, PopoverStyledButton, PopoverStyledPanel,
} from '@components/common';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { dataTestId } from '@services/data-test-id';


// Dynamic components (lazy loaded)
// *****************************************************
const CancelModal = dynamic(
  () => import('@components/templates/employer/subscription/cancel/ModalCancelSubscription')
    .then((mod) => mod.ModalCancelSubscription),
);


// Interface
// *****************************************************
interface SubscriptionInfoProps {
  className?: string,
  subscriptionStats?: ApiSchema<'PremiumSubscription'>,
  locale?: string,
}


/**
 * @description Subscription info component. It shows the current subscription info.
 * * **className** - additional classes for the component. Default: ''.
 * * **currentPackage** - the current subscription package data. Default: undefined.
 * * **renewalDate** - the subscription renewal date. Default: ''.
 */
export const SubscriptionInfo = (props: SubscriptionInfoProps) => {
  // Destructure props
  const {
    className = '',
    subscriptionStats,
    locale,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Modal states
  const { isOpen, toggleModal } = useBjModal();

  // SWR hook for global data. We need it to trigger the subscription stats mutation
  const { revalidateKey } = useSwrGlobal();

  // Success callback for the close request mutation
  const successCallback = () => {
    revalidateKey('/v1/subscription/stats');
  };

  // SWR hook for deleting the close request
  const {
    trigger: deleteCloseRequest, isMutating,
  } = useSwrDeleteCloseSubscription(locale, successCallback);


  // Return null if there is no current package
  // *****************************************************
  if (!subscriptionStats) return null;


  // Simplify variables for convenience
  // *****************************************************
  const currentPackage = subscriptionStats?.currentPackage;
  const renewalDate = subscriptionStats?.nextInvoiceDay;
  const closeDate = subscriptionStats?.closeRequestDate;

  const textRenewalExpiry = closeDate
    ? 'premium.subscription.expiry.date.label'
    : 'premium.subscription.renewal.date.label';


  // Render component
  // *****************************************************
  return (
    <>
      <PopoverStyled className={`relative ${className}`}>
        {/* First expiration date / button */}
        <PopoverStyledButton
          as={Button}
          styling="none"
          className="flex items-center gap-3.5 outline-0 sm:pr-4"
          {...dataTestId('premiumPage.openDropdownButton')}
        >
          <span className="flex size-7 items-center justify-center rounded-full border border-ink/15">
            <ChevronDownIcon className="size-4" />
          </span>
          <span className="text-nowrap text-left text-sm">
            {`${currentPackage?.mainTitle} - ${t('global.label.active')}`}
            <br />
            { closeDate && `${t(t('subscription.close-request-date.label'))} ${closeDate}`}
            {`${t(textRenewalExpiry)} ${renewalDate}`}
          </span>
        </PopoverStyledButton>

        {/* Rest of expiration dates */}
        <PopoverStyledPanel
          animation="slideDown"
          position="left"
          className="-mr-1.5 mt-2 px-3.5 py-1.5 text-sm sm:-ml-1"
        >
          { /* Open cancel request modal */
            !closeDate && (
              <div className="w-full">
                <Button
                  styling="text"
                  className="my-2 !text-ink"
                  onClick={toggleModal}
                  {...dataTestId('premiumPage.cancelSubscriptionButton')}
                >
                  {t('global.cancel.label')}
                </Button>
              </div>
            )
          }

          { /* Cancel close request. Shows only after the user cancels the subscription */
            closeDate && (
              <div className="w-full">
                <Button
                  styling="text"
                  className="my-2 !text-ink"
                  isLoading={isMutating}
                  onClick={() => {
                    void deleteCloseRequest();
                  }}
                >
                  {t('talent.subscription.action.delete_request')}
                </Button>
              </div>
            )
          }
        </PopoverStyledPanel>
      </PopoverStyled>

      {/* Cancel subscription modal */}
      <CancelModal
        isOpen={isOpen}
        toggleModal={toggleModal}
        currentPackage={currentPackage}
        renewalDate={renewalDate}
        locale={locale}
      />
    </>
  );
};
